import { useEffect, useState } from 'react';
import { Spin, Empty, Tooltip } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { $ } from '../lib/api';
import { fmtDateTime, fmtRelative, fmtTime } from '../lib/format';
import { useAuthStore } from '../store/auth';
import {
  PageHeader, HomeCard, FunnelRow, Btn, avatarSlot, Pill, Avatar, STAGE_KEYS, STAGE_SHORT,
} from '../components/ui';
import { StageSopCard } from '../components/StageSopCard';
import { QuotaCard } from '../components/protection/QuotaCard';
import { MyIncentiveCard } from '../components/MyIncentiveCard';
import { HealthAlertsCard } from '../components/protection/HealthAlertsCard';
import { LongCycleCard } from '../components/protection/LongCycleCard';
import { toast } from '../lib/toast';
// v1.5 阶段 4 P3：dashboard 卡片显示按权限点
import { useHasPermission, useHasAnyPermission } from '../lib/permissions';
// v1.5 C-1.b #8 修：Dashboard 自管 NewCustomerModal（不靠 Layout 事件，避免 canCreate 监听漏挂导致死按钮）
import { NewCustomerModal } from '../components/NewCustomerModal';
// Wave-Viz：工作台可视化卡片（viz1 今日待办聚合 / viz2 升级激励条 / viz3 团队排名红旗 / viz4 回款 vs 目标）
import { TodaySummaryCard } from '../components/viz/TodaySummaryCard';
import { UpgradeIncentiveStrip } from '../components/viz/UpgradeIncentiveStrip';
import { TeamRankCard } from '../components/viz/TeamRankCard';
import { ReceivableTargetCard } from '../components/viz/ReceivableTargetCard';

/* SVG icons for todo-item (matches v7 demo) */
const TodoIcon = {
  urgent: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 9v4M12 17h.01" /><circle cx="12" cy="12" r="10" /></svg>,
  warn: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>,
  normal: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 13l4 4L19 7" /></svg>,
};

/** Live countdown for a future ISO timestamp, displayed as HH:MM:SS.
 *  Returns null when target is in the past or not set. */
function useCountdown(target?: string | null): string | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);
  if (!target) return null;
  const diff = dayjs(target).valueOf() - now;
  if (diff <= 0) return null;
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  const s = Math.floor((diff % 60_000) / 1000);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function TodoMeta({ todo }: { todo: any }) {
  const cd = useCountdown(todo.dueAt);
  return (
    <div className="todo-meta">
      {cd && <><span className="urgent">⌛ {cd}</span> · </>}
      {todo.description}
    </div>
  );
}

// inc6：阶段标签统一改用 ui.tsx 的 STAGE_SHORT（含 L0/L5a/L5b 完整 10 档），删本地字典防漂移。

function greetTime() {
  const h = new Date().getHours();
  return h < 6 ? '凌晨好' : h < 12 ? '早上好' : h < 14 ? '中午好' : h < 18 ? '下午好' : '晚上好';
}

export function DashboardPage() {
  const user = useAuthStore((s) => s.user)!;
  const nav = useNavigate();
  // v1.5 C-1.b #8：Dashboard 自管 NewCustomerModal 显示
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const canCreateCustomer = useHasPermission('customer:create');

  /** One-time keyboard-shortcut hint toast (per browser). */
  useEffect(() => {
    if (!localStorage.getItem('huanke-tip-shown')) {
      setTimeout(() => {
        toast.info('提示：按 N 新增客户 · 1-8 切阶段 · 0 回工作台 · Esc 关闭弹窗', 6000);
        localStorage.setItem('huanke-tip-shown', '1');
      }, 800);
    }
  }, []);

  // P1-2: director 细分路由 —— 牛水停=data（漏斗/排行/转化）/ 程华庆=finance（发票/邮寄）
  // admin/director(null)/manager/sales 走通用工作台。
  // P1-2: 财务首页仪表盘仅 role==='director' 且 director_type='finance' 命中。
  //   (bug收尾批#5 评审：曾拟放宽为 directorType==='finance' 让程华庆 academic_director 命中，但该视图三个端点
  //    dashboard:director_view/report:view_team/report:view_all 程华庆均无→403 空壳，故维持原闸；其财务走财务工作台页。)
  if (user.role === 'director' && user.directorType === 'finance') {
    return <DirectorFinanceDashboardView userName={user.realName ?? ''} />;
  }
  if (user.role === 'director' && user.directorType === 'data') {
    return <DirectorDataDashboardView userName={user.realName ?? ''} />;
  }

  const role = user.role === 'sales' ? 'sales' : user.role === 'manager' ? 'manager' : 'director';
  const { data: kpi, isLoading } = useQuery({
    queryKey: ['dashboard', role],
    queryFn: () => $.get<any>(`/dashboard/${role}`),
  });
  // Sales-flavor data (todos + personal stats). Personal funnel may be empty for admin/director
  // since they don't own customers — so we additionally pull the GLOBAL funnel from /reports/funnel.
  const { data: salesKpi } = useQuery({
    queryKey: ['dashboard', 'sales-aux'],
    queryFn: () => $.get<any>('/dashboard/sales'),
  });
  // v1.5 P3：权限驱动视图分支
  // viewAsAll      = admin/director（全公司视图）→ report:view_all
  // viewAsTeam     = manager / 含 admin·director（团队视图，含 admin/director 因为 inheritance）→ report:view_team OR all
  // viewAsPersonal = sales（个人视图）= 没 team/all 视图权限
  const viewAsAll = useHasPermission('report:view_all');
  const isPrivileged = useHasAnyPermission(['report:view_team', 'report:view_all']);
  const viewAsTeam = isPrivileged && !viewAsAll;
  const viewAsPersonal = !isPrivileged;
  const { data: globalFunnel } = useQuery({
    queryKey: ['reports-funnel'],
    queryFn: () => $.get<Array<{ stage: string; cnt: number }>>('/reports/funnel'),
    enabled: isPrivileged,
  });
  const { data: todos } = useQuery({
    queryKey: ['daily-todos'],
    queryFn: () => $.get<any[]>('/daily-todos/today'),
  });

  if (isLoading) return <Spin size="large" style={{ margin: '120px auto', display: 'block' }} />;
  if (!kpi) return <Empty />;

  const todayCount = todos?.length ?? 0;
  const p0 = todos?.filter((t: any) => t.priority === 'P0').length ?? 0;
  // For admin/director/manager use the company-wide funnel; for sales their personal pipeline.
  const funnel = (isPrivileged ? globalFunnel : (salesKpi?.funnel ?? kpi.funnel))
    ?? [] as Array<{ stage: string; cnt: number }>;
  // Banner 显示维度：sales=个人，manager=团队，director/admin=全公司。
  // 明确不混着取数 —— 上一轮 admin 看到的 ¥7.9 万其实是全公司数据，但 banner
  // 没标注，演示时被吐槽。
  const isPersonal = viewAsPersonal;
  const isDirectorView = viewAsAll;
  const dimensionLabel = isPersonal ? '我的' : isDirectorView ? '全公司' : '团队';
  const monthRev = (isPersonal
    ? salesKpi?.kpi?.monthRevenue
    : kpi.monthRevenue ?? salesKpi?.kpi?.monthRevenue) ?? 0 as number;
  const monthOrders = (isPersonal
    ? salesKpi?.kpi?.monthOrders
    : kpi.monthDeals ?? salesKpi?.kpi?.monthOrders) ?? 0 as number;
  // 之前不管金额多小都显示 "¥0k" / "¥1k"，给人"系统坏了"的错觉。
  // 改为：0 直接显示"暂无"，<1 万显示整千，>=1 万显示万。
  const monthRevText = monthRev === 0
    ? '暂无'
    : monthRev >= 10000
      ? `¥${(monthRev / 10000).toFixed(1)}万`
      : `¥${Math.round(monthRev / 1000)}k`;
  const msg = p0 > 0
    ? `今天有 ${p0} 个 P0 客户需要立即处理。先解决这些，再处理后面的。`
    : todayCount > 0
      ? `今天有 ${todayCount} 个待办，节奏稳一点。`
      : '今天没有紧急待办——主动挖几个 L3-L4 客户的需求，是最值得的事。';

  return (
    <>
      <PageHeader
        breadcrumb={['HuanKe', '工作台']}
        title="工作台"
        subtitle="从这里开始你的一天"
        actions={
          <>
            <Btn kind="default" onClick={() => nav('/customers')}>查看客户</Btn>
            {/* v1.5 C-1.b #8 修：直接打开 modal，不靠全局事件（避免 Layout canCreate 监听漏挂导致按钮死）*/}
            {canCreateCustomer && (
              <Btn kind="primary" onClick={() => setShowNewCustomer(true)}>
                新增客户
              </Btn>
            )}
          </>
        }
      />
      <div className="work-content">
        {/* viz2：升级激励摘要条，挂工作台顶部（仅 sales），与中部 MyIncentiveCard 完整卡共存 */}
        {user.role === 'sales' && <UpgradeIncentiveStrip />}
        <HomeBanner
          userName={user.realName ?? ''}
          msg={msg}
          // 今日待办（个人）/ 本月成交 / 本月营收 — 后两项按 dimensionLabel 区分
          stats={[
            { label: '今日待办', value: todayCount },
            // 任务6：成交/营收两个 KPI 已切到 approved_at 严格口径，hint 解释口径防"数字变了"误解
            //   (口径=按审批通过时间统计已通过订单、金额与提成一致；单数含启航班、判级单量另去启航，故成交单数可能略多于判级口径)
            { label: `${dimensionLabel}本月成交`, value: monthOrders, hint: '口径：按审批通过时间(approved_at)统计已审批通过的订单；金额与提成一致' },
            { label: `${dimensionLabel}本月营收`, value: monthRevText, hint: '口径：按审批通过时间(approved_at)统计已审批通过的订单；金额与提成一致' },
          ]}
        />
        {/* viz1：今日待办聚合卡（待跟进/待开票/待收款/待审批），仅 sales，挂个人卡片之上 */}
        {user.role === 'sales' && <TodaySummaryCard />}
        {/* v1.5 P3：sales 视图（无 report:view_team/all 权限的个人视图）显示个人卡片 */}
        {viewAsPersonal && <SalesTargetCard />}
        {viewAsPersonal && <QuotaCard />}
        {/* 薪酬阶段3：个人激励只对 sales 显示（判级群体=sales；不挂 viewAsPersonal——sales 有 report:view_team(seed0013) 致其=false） */}
        {user.role === 'sales' && <MyIncentiveCard />}
        {viewAsPersonal && (
          <div className="home-grid">
            <HealthAlertsCard />
            <LongCycleCard />
          </div>
        )}
        {viewAsPersonal && (
          <div className="home-grid">
            <SprintLeaderboardCard />
            <MyDailyReportCard userName={user.realName ?? ''} />
          </div>
        )}
        {viewAsPersonal && <StageSopCard mode="compact" initialStage="L3" />}
        {/* manager+ 显示告警行 */}
        {isPrivileged && <AlertsRow />}
        {/* viz4：本月回款 vs 目标进度卡。门控改【权限点驱动】dashboard:finance_workbench——admin/director(经all)/manager 持有，
            财务总监程华庆(academic_director+director_type=finance)经 seed 0032 用户级补授后可见(财务+教务都管);房启飞(纯教务 academic_director)无点不可见。后端同权限点+scope 双闸。*/}
        {useHasPermission('dashboard:finance_workbench') && <ReceivableTargetCard />}
        {/* viz3：销售排名 + 掉队红旗 + 健康度恶化（仅 manager/director/admin；sales 即便持 view_team 也被后端 Forbidden 挡，前端再叠 role 门控）*/}
        {isPrivileged && ['manager', 'director', 'admin'].includes(user.role) && <TeamRankCard />}
        {/* admin/director 不显示个人今日必做（看全公司大盘）*/}
        <CoreWidgets
          todos={viewAsAll ? [] : (todos ?? [])}
          funnel={funnel}
          hideTodos={viewAsAll}
        />
        <TeamActivity />
        {/* manager-only 团队成员卡片：有 team 视图但无 all 视图 */}
        {viewAsTeam && <ManagerExtras kpi={kpi} />}
      </div>
      {/* v1.5 C-1.b #8：Dashboard 自管 NewCustomerModal */}
      {canCreateCustomer && (
        <NewCustomerModal open={showNewCustomer} onClose={() => setShowNewCustomer(false)} />
      )}
    </>
  );
}

/** 销售员本月业绩进度卡 — 仅 sales 角色显示 */
function SalesTargetCard() {
  const { data, isLoading } = useQuery<any>({
    queryKey: ['sales-targets/my/current'],
    queryFn: () => $.get('/sales-targets/my/current'),
  });
  if (isLoading || !data) return null;
  if (!data.hasTarget) {
    return (
      <HomeCard title={<>本月业绩 <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>等主管设置目标</span></>}>
        <div style={{ padding: 16, color: 'var(--text-3)', fontSize: 13 }}>
          您的 {data.year}年{data.month}月业绩目标尚未设置 — 请联系主管。
        </div>
      </HomeCard>
    );
  }
  const rate = data.completionRate;
  const ratePct = (rate * 100).toFixed(1);
  const rateColor =
    rate >= 1 ? 'var(--success)' :
    rate >= 0.8 ? '#1677ff' :
    rate >= 0.5 ? 'var(--warn)' : 'var(--danger)';
  const fmt = (n: number) => '¥' + (n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
  const urgent = data.daysLeft <= 5 && rate < 1;
  return (
    <HomeCard
      title={<>本月业绩 <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>{data.year}年{data.month}月</span></>}
    >
      <div style={{ padding: '12px 16px 16px' }}>
        {urgent && (
          <div style={{
            background: 'rgba(255,77,79,0.08)', color: 'var(--danger)',
            padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13, fontWeight: 500,
          }}>
            ⚠️ 距月底仅剩 {data.daysLeft} 天，完成率 {ratePct}% — 每日需 {fmt(data.dailyNeed)}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <div>
            <span style={{ fontSize: 24, fontWeight: 600, color: rateColor }}>{fmt(data.actualAmount)}</span>
            <span style={{ marginLeft: 8, fontSize: 13, color: 'var(--text-3)' }}>/ {fmt(data.targetAmount)}</span>
          </div>
          <div style={{ fontSize: 14, fontWeight: 500, color: rateColor }}>{ratePct}%</div>
        </div>
        <div style={{ background: 'var(--bg-3)', height: 10, borderRadius: 5, overflow: 'hidden', marginBottom: 12 }}>
          <div
            style={{
              width: `${Math.min(100, rate * 100)}%`, height: '100%',
              background: rateColor, transition: 'width 0.4s',
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: 24, fontSize: 13, color: 'var(--text-2)' }}>
          <div>
            <div style={{ color: 'var(--text-3)', fontSize: 12 }}>还差</div>
            <div style={{ fontWeight: 500 }}>{data.remaining > 0 ? fmt(data.remaining) : '✓ 已达成'}</div>
          </div>
          <div>
            <div style={{ color: 'var(--text-3)', fontSize: 12 }}>距月底</div>
            <div style={{ fontWeight: 500 }}>{data.daysLeft} 天</div>
          </div>
          {data.remaining > 0 && (
            <div>
              <div style={{ color: 'var(--text-3)', fontSize: 12 }}>日均需</div>
              <div style={{ fontWeight: 500 }}>{fmt(data.dailyNeed)}</div>
            </div>
          )}
        </div>
      </div>
    </HomeCard>
  );
}

/** P0-3: 管理者预警卡（admin / director / manager 共用，后端按 actor.role 自动 scope）
 *  B0：首列加「待我审批(N)」，数据复用 /approvals/pending 的 total（按 currentApproverId=我 计数） */
function AlertsRow() {
  // bug1 真凶修：sales 经 seed 0013 持 report:view_team 致 isPrivileged=true、会渲染本管理预警卡，
  //   而「待我审批」拉 /approvals/pending(守卫 approval:view)→sales 无点→500「无权访问」。
  //   用 enabled 按 approval:view 守卫该请求(sales 不发)，并让「待我审批」格仅持点者显示(sales 不显该格、不弹窗)。
  const canViewApproval = useHasPermission('approval:view');
  const { data } = useQuery<any>({
    queryKey: ['dashboard/alerts'],
    queryFn: () => $.get('/dashboard/alerts'),
  });
  const { data: mine } = useQuery<any>({
    queryKey: ['approvals/pending-count'],
    queryFn: () => $.get('/approvals/pending', { page: 1, pageSize: 1 }),
    refetchInterval: 30_000,
    enabled: canViewApproval,
  });
  if (!data) return null;
  const items = [
    ...(canViewApproval ? [{ label: '待我审批', value: mine?.total ?? 0, color: '#1677ff', emoji: '📋', link: '/approvals' }] : []),
    { label: '超 7 天未跟进', value: data.staleFollowUp, color: 'var(--danger)', emoji: '🔴', link: '/customers?stale=7' },
    { label: '完成率<30% 销售员', value: data.laggingSales, color: 'var(--danger)', emoji: '🔴', link: '/admin/sales-targets' },
    { label: '即将进公海', value: data.poolWarning, color: '#fa8c16', emoji: '🟡', link: '/customers?status=probation' },
    { label: '审批超时 24h+', value: data.approvalOverdue, color: '#fa8c16', emoji: '🟡', link: '/approvals' },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: 12, marginBottom: 16 }}>
      {items.map((it) => (
        <Link key={it.label} to={it.link} style={{ textDecoration: 'none' }}>
          <div style={{
            background: 'var(--bg-1)',
            border: `1px solid ${it.value > 0 ? it.color : 'var(--border)'}`,
            borderRadius: 8, padding: '16px 18px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            transition: 'transform 0.15s, box-shadow 0.15s',
            cursor: 'pointer',
          }}>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>
              <span style={{ marginRight: 4 }}>{it.emoji}</span>
              {it.label}
            </div>
            <div style={{
              fontSize: 26, fontWeight: 600,
              color: it.value > 0 ? it.color : 'var(--text-2)',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {it.value ?? 0}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

/** B0：待我审批数字卡（独立查询，给不渲染 AlertsRow 的视图用，如财务总监工作台） */
function MyApprovalsCard() {
  const { data: mine } = useQuery<any>({
    queryKey: ['approvals/pending-count'],
    queryFn: () => $.get('/approvals/pending', { page: 1, pageSize: 1 }),
    refetchInterval: 30_000,
  });
  const value = mine?.total ?? 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
      <Link to="/approvals" style={{ textDecoration: 'none' }}>
        <div style={{
          background: 'var(--bg-1)',
          border: `1px solid ${value > 0 ? '#1677ff' : 'var(--border)'}`,
          borderRadius: 8, padding: '16px 18px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)', cursor: 'pointer',
        }}>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>
            <span style={{ marginRight: 4 }}>📋</span>待我审批
          </div>
          <div style={{
            fontSize: 26, fontWeight: 600,
            color: value > 0 ? '#1677ff' : 'var(--text-2)',
            fontVariantNumeric: 'tabular-nums',
          }}>
            {value}
          </div>
        </div>
      </Link>
    </div>
  );
}

/** P0-2: 按 category 分组的今日待办 */
const CATEGORY_META: Record<string, { label: string; emoji: string; color: string }> = {
  urgent:        { label: '紧急',        emoji: '🔴', color: 'var(--danger)' },
  pool_warning:  { label: '即将进公海', emoji: '🟠', color: '#fa8c16' },
  sign_contract: { label: '该签合同了', emoji: '🟡', color: '#faad14' },
  regular:       { label: '常规跟进',   emoji: '🟢', color: 'var(--success)' },
  info:          { label: '提醒',        emoji: '💡', color: '#1677ff' },
};
const CATEGORY_ORDER = ['urgent', 'pool_warning', 'sign_contract', 'regular', 'info'];

/** Core v7 widgets — always rendered, regardless of role */
function CoreWidgets({ todos, funnel, hideTodos }: { todos: any[]; funnel: Array<{ stage: string; cnt: number }>; hideTodos?: boolean }) {
  const maxCnt = Math.max(1, ...(funnel.map((f: { cnt: number }) => Number(f.cnt))));
  // 按 category 分组
  const grouped: Record<string, any[]> = {};
  for (const t of todos) {
    const cat = t.category ?? 'regular';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(t);
  }
  return (
    <div className="home-grid">
      {!hideTodos && (
      <HomeCard
        title={<>今日必做 <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>按紧急度智能分组</span></>}
        action={<Link to="/customers">查看全部</Link>}
      >
        {todos.length === 0 && (
          <div style={{ padding: '32px 18px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
            今天没有紧急待办
          </div>
        )}
        {CATEGORY_ORDER.map((cat) => {
          const items = grouped[cat];
          if (!items || items.length === 0) return null;
          const meta = CATEGORY_META[cat];
          return (
            <div key={cat} style={{ marginBottom: 8 }}>
              <div style={{
                padding: '8px 14px', fontSize: 12, fontWeight: 500,
                color: meta.color, background: 'var(--bg-2)',
                borderLeft: `3px solid ${meta.color}`,
              }}>
                {meta.emoji} {meta.label}
                <span style={{ marginLeft: 6, color: 'var(--text-3)', fontWeight: 400 }}>({items.length})</span>
              </div>
              {items.slice(0, 5).map((t: any) => {
                const tone = t.priority === 'P0' ? 'urgent' : t.priority === 'P1' ? 'warn' : 'normal';
                const actionLabel = cat === 'sign_contract' ? '去签单'
                  : cat === 'urgent' ? '立即处理'
                  : cat === 'pool_warning' ? '立即抢救'
                  : cat === 'info' ? '查看' : '立即跟进';
                const inner = (
                  <div className="todo-item">
                    <div className={`todo-icon ${tone}`}>{TodoIcon[tone as keyof typeof TodoIcon]}</div>
                    <div className="todo-content">
                      <div className="todo-title">{t.title}</div>
                      <TodoMeta todo={t} />
                    </div>
                    {t.linkUrl && <span className="todo-action">{actionLabel}</span>}
                  </div>
                );
                return t.linkUrl
                  ? <Link key={t.id} to={t.linkUrl}>{inner}</Link>
                  : <div key={t.id}>{inner}</div>;
              })}
            </div>
          );
        })}
      </HomeCard>
      )}

      {/* 漏斗口径(程伟拍 A)：本卡是 current_stage【当前阶段快照占用数】、非累计"已达≥该阶段"，故不单调(L2 可>L1)。
          改标题为"客户阶段分布(当前快照)"+免责副标，当分布看、不当漏斗看(真累计漏斗 B 方案以后单独排)。 */}
      <HomeCard title={<>客户阶段分布（当前快照）<span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>当前阶段快照 · 不受时间筛选影响</span></>} action={<Link to="/customers">详情</Link>}>
        <div className="funnel">
          {STAGE_KEYS.map((stage) => {
            // inc6：完整 10 档（含 L0、L5a/L5b 拆开不合并），精确匹配
            const cnt = funnel
              .filter((f) => f.stage === stage)
              .reduce((s, f) => s + Number(f.cnt), 0);
            const rate = maxCnt > 0 ? Math.round((cnt / maxCnt) * 100) : 0;
            return (
              <FunnelRow
                key={stage}
                stage={stage}
                label={STAGE_SHORT[stage]}
                count={cnt}
                rate={rate}
                max={maxCnt}
              />
            );
          })}
        </div>
      </HomeCard>
    </div>
  );
}

function ManagerExtras({ kpi }: any) {
  return (
    <HomeCard title="团队成员" action={<Link to="/team">管理团队</Link>}>
      {(kpi.members ?? []).map((m: any) => {
        const pct = m.capacity > 0 ? Math.round((m.customers / m.capacity) * 100) : 0;
        return (
          <div key={m.id} className="todo-item" style={{ paddingTop: 12, paddingBottom: 12 }}>
            <Avatar userId={m.id} src={m.avatarUrl} name={m.name} size="md" />
            <div className="todo-content">
              <div className="todo-title">
                {m.name} {m.isProbation && <Pill tone="warning">试用期</Pill>}
              </div>
              <div className="todo-meta">持客 {m.customers}/{m.capacity} · 使用率 {pct}%</div>
            </div>
            <Link to="/team" className="todo-action">查看</Link>
          </div>
        );
      })}
      {(!kpi.members || kpi.members.length === 0) && (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>暂无团队成员</div>
      )}
    </HomeCard>
  );
}

function HomeBanner({
  userName, msg, stats,
}: {
  userName: string;
  msg: string;
  stats: Array<{ label: string; value: string | number; hint?: string }>;
}) {
  return (
    <div className="home-banner">
      <div className="home-banner-content">
        <div className="home-greeting">
          <div className="home-greet-time">{greetTime()} · {dayjs().format('M 月 D 日 · dddd')}</div>
          <div className="home-greet-name">{userName}</div>
          <div className="home-greet-msg">{msg}</div>
        </div>
        <div className="home-stats-mini">
          {stats.map((s, i) => (
            <div key={i} className="home-stat-mini">
              <div className="home-stat-mini-label">
                {/* 任务6：带 hint 的 KPI 标签挂 Tooltip 说明统计口径 */}
                {s.hint ? (
                  <Tooltip title={s.hint}>
                    <span style={{ cursor: 'help', borderBottom: '1px dashed currentColor' }}>{s.label}</span>
                  </Tooltip>
                ) : s.label}
              </div>
              <div className="home-stat-mini-value">{s.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Team activity = recent notifications (system events). Matches scene-home demo's bottom card. */
function TeamActivity() {
  const { data } = useQuery({
    queryKey: ['notifications-feed'],
    queryFn: () => $.get<{ items: any[] }>('/notifications', { page: 1, pageSize: 6 }),
  });
  const items = data?.items ?? [];
  return (
    <HomeCard title="团队动态" action={<Link to="/notifications">查看全部</Link>}>
      {items.length === 0 && (
        <div style={{ padding: '20px 18px', color: 'var(--text-3)', fontSize: 13 }}>暂无动态</div>
      )}
      {items.map((n: any) => {
        const slot = avatarSlot(n.userId ?? n.id);
        const gradient = AVATAR_GRADIENTS[slot];
        const initial = (n.title ?? '·').slice(0, 1);
        const inner = (
          <div className="activity-item">
            <div className="activity-avatar" style={{ background: gradient }}>{initial}</div>
            <div className="activity-content">
              <div className="activity-text">
                <strong>{n.title}</strong>
                {n.content && <> · {n.content}</>}
              </div>
              <div className="activity-time" data-tip={fmtDateTime(n.createdAt)}>
                {fmtRelative(n.createdAt)}
              </div>
            </div>
          </div>
        );
        return n.linkUrl ? <Link key={n.id} to={n.linkUrl}>{inner}</Link> : <div key={n.id}>{inner}</div>;
      })}
    </HomeCard>
  );
}

/** P2-3 业绩冲刺榜卡片 —— sales 看本团队完成率排名，月底 5 天进入冲刺模式 */
function SprintLeaderboardCard() {
  const { data } = useQuery<any>({
    queryKey: ['sales-targets/leaderboard'],
    queryFn: () => $.get('/sales-targets/leaderboard'),
  });
  if (!data || !data.items || data.items.length === 0) return null;
  const sprint: boolean = data.isSprint;
  const daysLeft = data.daysLeft;
  return (
    <HomeCard
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>业绩冲刺榜</span>
          {sprint && (
            <span style={{
              fontSize: 11, padding: '2px 8px', background: 'var(--danger)',
              color: '#fff', borderRadius: 10, fontWeight: 600,
            }}>🔥 冲刺中 · {daysLeft}天</span>
          )}
        </div>
      }
      action={<Link to="/admin/sales-targets">详情</Link>}
    >
      <div style={{ padding: '4px 0' }}>
        {data.items.slice(0, 8).map((it: any) => {
          const rate = Math.min(100, Math.round((it.completionRate ?? 0) * 100));
          const isTop3 = it.rank <= 3;
          return (
            <div key={it.userId} style={{
              padding: '10px 16px', borderBottom: '1px solid var(--border)',
              background: it.isSelf ? 'rgba(124,77,255,0.06)' : 'transparent',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{
                width: 24, height: 24, borderRadius: 12, lineHeight: '24px',
                textAlign: 'center',
                background: isTop3 ? '#fa8c16' : 'var(--bg-3)',
                color: isTop3 ? '#fff' : 'var(--text-2)',
                fontSize: 12, fontWeight: 600,
              }}>{it.rank}</span>
              <Avatar userId={it.userId} src={it.avatarUrl} name={it.name} size="sm" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13 }}>
                  {it.name}{it.isSelf && <span style={{ color: 'var(--purple)', marginLeft: 4 }}>· 你</span>}
                </div>
                <div style={{ background: 'var(--bg-3)', height: 5, borderRadius: 3, marginTop: 4 }}>
                  <div style={{
                    width: `${rate}%`, height: '100%',
                    background: rate >= 100 ? 'var(--success)' : rate >= 50 ? '#1677ff' : 'var(--warn)',
                    borderRadius: 3,
                  }} />
                </div>
              </div>
              <span style={{
                fontSize: 12, fontVariantNumeric: 'tabular-nums', minWidth: 40, textAlign: 'right',
                color: rate >= 100 ? 'var(--success)' : 'var(--text-2)', fontWeight: 500,
              }}>{rate}%</span>
            </div>
          );
        })}
      </div>
    </HomeCard>
  );
}

/** P2-7 我的日报卡片 —— sales 看今天的动作汇总 + 一键发钉钉 */
function MyDailyReportCard({ userName }: { userName: string }) {
  const qc = useQueryClient();
  const { data } = useQuery<any>({
    queryKey: ['daily-reports/me/today'],
    queryFn: () => $.get('/daily-reports/me/today'),
  });
  const send = useMutation({
    mutationFn: () => $.post('/daily-reports/me/send-to-dingtalk', {}),
    onSuccess: () => {
      toast.success('已发送到钉钉');
      qc.invalidateQueries({ queryKey: ['daily-reports/me/today'] });
    },
    onError: (e: any) => toast.error('发送失败：' + (e?.message ?? '未知错误')),
  });
  if (!data) return null;
  const rev = Number(data.revenue ?? 0);
  const revText = rev >= 10000 ? `¥${(rev / 10000).toFixed(1)}万` : `¥${Math.round(rev / 1000)}k`;
  const sent = !!data.sentToDingtalkAt;
  return (
    <HomeCard
      title={<>我的日报 <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>{userName} · {dayjs().format('M 月 D 日')}</span></>}
      action={
        <a
          onClick={(e) => { e.preventDefault(); if (!send.isPending) send.mutate(); }}
          style={{ color: sent ? 'var(--text-3)' : 'var(--purple)', cursor: 'pointer' }}
        >
          {sent ? `✓ ${fmtTime(data.sentToDingtalkAt)} 已发` : send.isPending ? '发送中…' : '📨 发钉钉'}
        </a>
      }
    >
      <div style={{ padding: '12px 16px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        <DailyKv label="新增" value={data.newCustomers ?? 0} />
        <DailyKv label="跟进" value={data.followUps ?? 0} />
        <DailyKv label="推进" value={data.stageAdvances ?? 0} />
        <DailyKv label="成交" value={`${data.deals ?? 0} 单`} subtext={rev > 0 ? revText : ''} />
      </div>
      {Array.isArray(data.highlights) && data.highlights.length > 0 && (
        <div style={{ padding: '0 16px 14px' }}>
          {data.highlights.map((h: any, i: number) => (
            <div key={i} style={{
              padding: '6px 10px', fontSize: 12, color: 'var(--warning-text)',
              background: 'rgba(250,140,22,0.08)', borderRadius: 4, marginTop: 4,
            }}>
              ✨ {h.text ?? JSON.stringify(h)}
            </div>
          ))}
        </div>
      )}
      {(!data.newCustomers && !data.followUps && !data.stageAdvances && !data.deals) && (
        <div style={{
          padding: '0 16px 14px', fontSize: 12, color: 'var(--text-3)', fontStyle: 'italic',
        }}>
          今天还没开始动作，加油 💪
        </div>
      )}
    </HomeCard>
  );
}

function DailyKv({ label, value, subtext }: { label: string; value: string | number; subtext?: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      {subtext && <div style={{ fontSize: 11, color: 'var(--success)' }}>{subtext}</div>}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * P1-2: director 子角色仪表盘
 *
 * 之前 director（牛水停=看数据, 程华庆=开发票）共用 admin 仪表盘，
 * 两人需求南辕北辙：牛水停要漏斗/转化/排行，程华庆要发票/邮寄/收款。
 * 强行混在一起既冗余又分散注意力 —— 拆成两个独立视图，按 user.directorType
 * 路由（见 DashboardPage 顶部）。两者都不显示个人"今日必做"。
 * ────────────────────────────────────────────────────────────────────── */

/** 程华庆（director_type='finance'）的财务/发票视角仪表盘 */
function DirectorFinanceDashboardView({ userName }: { userName: string }) {
  const nav = useNavigate();
  const { data: fin } = useQuery<any>({
    queryKey: ['dashboard/director-finance'],
    queryFn: () => $.get('/dashboard/director-finance'),
  });
  const { data: globalFunnel } = useQuery({
    queryKey: ['reports-funnel'],
    queryFn: () => $.get<Array<{ stage: string; cnt: number }>>('/reports/funnel'),
  });
  const { data: channelRoi } = useQuery<any[]>({
    queryKey: ['reports-channel-roi'],
    queryFn: () => $.get('/reports/channel-roi'),
  });
  // v1.2-rc2 #2：0 元不再渲染成 "¥0k"，统一显示 "¥0"
  const fmt = (n: number) =>
    !n ? '¥0'
      : n >= 10000 ? `¥${(n / 10000).toFixed(1)}万`
      : `¥${(n / 1000).toFixed(0)}k`;
  const cards = [
    { label: '待开发票', value: fin?.pendingInvoiceCount ?? 0, color: 'var(--danger)', emoji: '📄', link: '/invoices?status=pending' },
    { label: '已开未送达', value: fin?.shipmentPendingCount ?? 0, color: '#fa8c16', emoji: '📦', link: '/shipments?status=pending' },
    { label: '本月开票笔数', value: fin?.monthIssuedCount ?? 0, color: '#1677ff', emoji: '🧾', link: '/invoices' },
    { label: '本月开票金额', value: fmt(Number(fin?.monthIssuedAmount ?? 0)), color: 'var(--success)', emoji: '💰', link: '/invoices' },
  ];
  return (
    <>
      <PageHeader
        breadcrumb={['HuanKe', '财务工作台']}
        title="财务工作台"
        subtitle={`${userName} · ${greetTime()} · ${dayjs().format('M 月 D 日 · dddd')}`}
        actions={
          <>
            <Btn kind="default" onClick={() => nav('/invoices')}>开发票</Btn>
            <Btn kind="primary" onClick={() => nav('/shipments')}>邮寄管理</Btn>
          </>
        }
      />
      <div className="work-content">
        {/* B0：财务总监工作台不渲染 AlertsRow，单独补「待我审批」卡（财务也是审批人） */}
        <MyApprovalsCard />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
          {cards.map((it) => (
            <Link key={it.label} to={it.link} style={{ textDecoration: 'none' }}>
              <div style={{
                background: 'var(--bg-1)',
                border: `1px solid ${Number(it.value) > 0 ? it.color : 'var(--border)'}`,
                borderRadius: 8, padding: '16px 18px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)', cursor: 'pointer',
              }}>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>
                  <span style={{ marginRight: 4 }}>{it.emoji}</span>
                  {it.label}
                </div>
                <div style={{
                  fontSize: 26, fontWeight: 600,
                  color: Number(it.value) > 0 ? it.color : 'var(--text-2)',
                  fontVariantNumeric: 'tabular-nums',
                }}>{it.value}</div>
              </div>
            </Link>
          ))}
        </div>

        {/* viz4：本月回款 vs 目标进度卡（财务总监工作台亦展示；目标缺失显「未设置」）*/}
        <ReceivableTargetCard />

        <HomeCard title="发票分类（本月）" action={<Link to="/invoices">详情</Link>}>
          <div style={{ padding: '12px 16px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {['receipt', 'general', 'special'].map((t) => {
              const row = (fin?.invoiceByType ?? []).find((r: any) => r.type === t);
              const labelMap: Record<string, string> = {
                receipt: '收据', general: '增值税普票', special: '增值税专票',
              };
              return (
                <div key={t} style={{
                  padding: 14, background: 'var(--bg-2)', borderRadius: 6,
                }}>
                  <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>{labelMap[t]}</div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>
                    {row?.count ?? 0} 笔 · {fmt(Number(row?.amount ?? 0))}
                  </div>
                </div>
              );
            })}
          </div>
        </HomeCard>

        <div className="home-grid">
          {/* 同上(程伟拍 A)：全公司版同为当前阶段快照占用数、非累计漏斗，一并改标题+免责副标保持一致。 */}
          <HomeCard title={<>客户阶段分布（全公司·当前快照）<span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>当前阶段快照 · 不受时间筛选影响</span></>} action={<Link to="/customers">详情</Link>}>
            <div className="funnel">
              {STAGE_KEYS.map((stage) => {
                const cnt = (globalFunnel ?? [])
                  .filter((f) => f.stage === stage)
                  .reduce((s, f) => s + Number(f.cnt), 0);
                const max = Math.max(1, ...(globalFunnel ?? []).map((f) => Number(f.cnt)));
                const rate = max > 0 ? Math.round((cnt / max) * 100) : 0;
                return (
                  <FunnelRow key={stage} stage={stage} label={STAGE_SHORT[stage]}
                    count={cnt} rate={rate} max={max} />
                );
              })}
            </div>
          </HomeCard>

          <HomeCard title="渠道 ROI">
            <div style={{ padding: '8px 16px 16px' }}>
              {(channelRoi ?? []).length === 0 && (
                <div style={{ padding: '20px 0', color: 'var(--text-3)', fontSize: 13, textAlign: 'center' }}>
                  暂无渠道数据
                </div>
              )}
              {(channelRoi ?? []).map((r: any) => {
                const total = Number(r.total ?? 0);
                const deals = Number(r.deals ?? 0);
                const pct = total > 0 ? Math.round((deals / total) * 100) : 0;
                return (
                  <div key={r.source ?? 'unknown'} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span>{r.source ?? '未知'}</span>
                      <span style={{ color: 'var(--text-3)' }}>{deals} / {total} · {pct}%</span>
                    </div>
                    <div style={{ background: 'var(--bg-3)', height: 6, borderRadius: 3 }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: 'var(--success)', borderRadius: 3 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </HomeCard>
        </div>
      </div>
    </>
  );
}

/** 牛水停（director_type='data'）的数据/转化分析视角仪表盘 */
function DirectorDataDashboardView({ userName }: { userName: string }) {
  const nav = useNavigate();
  const { data: kpi } = useQuery<any>({
    queryKey: ['dashboard/director'],
    queryFn: () => $.get('/dashboard/director'),
  });
  const { data: funnel } = useQuery({
    queryKey: ['reports-funnel'],
    queryFn: () => $.get<Array<{ stage: string; cnt: number }>>('/reports/funnel'),
  });
  const { data: ranking } = useQuery<any[]>({
    queryKey: ['reports-sales-ranking'],
    queryFn: () => $.get('/reports/sales-ranking'),
  });
  const { data: avgCycle } = useQuery<any>({
    queryKey: ['reports-avg-cycle'],
    queryFn: () => $.get('/reports/avg-deal-cycle'),
  });
  // v1.2-rc2 #2：0 元不再渲染成 "¥0k"，统一显示 "¥0"
  const fmt = (n: number) =>
    !n ? '¥0'
      : n >= 10000 ? `¥${(n / 10000).toFixed(1)}万`
      : `¥${(n / 1000).toFixed(0)}k`;
  // 漏斗转化率（按相邻阶段；inc6 完整 10 档含 L0、L5a/L5b 拆开）
  const stages = STAGE_KEYS;
  const stageCnt = (st: string) => (funnel ?? [])
    .filter((f) => f.stage === st)
    .reduce((s, f) => s + Number(f.cnt), 0);
  const conversion = stages.slice(0, -1).map((from, i) => {
    const to = stages[i + 1];
    const a = stageCnt(from), b = stageCnt(to);
    return { from, to, rate: a > 0 ? Math.round((b / a) * 100) : 0, fromCnt: a, toCnt: b };
  });
  return (
    <>
      <PageHeader
        breadcrumb={['HuanKe', '数据工作台']}
        title="数据工作台"
        subtitle={`${userName} · ${greetTime()} · ${dayjs().format('M 月 D 日 · dddd')}`}
        actions={<Btn kind="default" onClick={() => nav('/customers')}>查看客户</Btn>}
      />
      <div className="work-content">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
          {[
            { label: '全公司客户', value: kpi?.totalCustomers ?? 0, color: '#1677ff' },
            { label: '本月成交单数', value: kpi?.monthDeals ?? 0, color: 'var(--success)' },
            { label: '本月营收', value: fmt(Number(kpi?.monthRevenue ?? 0)), color: 'var(--success)' },
            { label: '平均成单周期', value: `${Math.round(Number(avgCycle?.avgDays ?? 0))} 天`, color: '#fa8c16' },
          ].map((it) => (
            <div key={it.label} style={{
              background: 'var(--bg-1)', border: '1px solid var(--border)',
              borderRadius: 8, padding: '16px 18px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>{it.label}</div>
              <div style={{ fontSize: 26, fontWeight: 600, color: it.color, fontVariantNumeric: 'tabular-nums' }}>
                {it.value}
              </div>
            </div>
          ))}
        </div>

        <AlertsRow />

        <div className="home-grid">
          <HomeCard title="漏斗转化率" action={<Link to="/customers">详情</Link>}>
            <div style={{ padding: '8px 16px 16px' }}>
              {conversion.map((c) => (
                <div key={c.from} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span>{STAGE_SHORT[c.from]} → {STAGE_SHORT[c.to]}</span>
                    <span style={{
                      color: c.rate >= 50 ? 'var(--success)' : c.rate >= 30 ? '#fa8c16' : 'var(--danger)',
                      fontWeight: 500,
                    }}>{c.rate}%</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                    {c.fromCnt} → {c.toCnt}
                  </div>
                </div>
              ))}
            </div>
          </HomeCard>

          <HomeCard title="销售员排行（本月）" action={<Link to="/admin/sales-targets">详情</Link>}>
            <div style={{ padding: '4px 0' }}>
              {(ranking ?? []).filter((r: any) => r.role === 'sales').slice(0, 8).map((r: any, i: number) => (
                <div key={r.userId} style={{
                  display: 'flex', alignItems: 'center', padding: '10px 16px',
                  borderBottom: '1px solid var(--border)',
                }}>
                  <span style={{
                    width: 24, height: 24, borderRadius: 12, lineHeight: '24px', textAlign: 'center',
                    background: i < 3 ? '#fa8c16' : 'var(--bg-3)',
                    color: i < 3 ? '#fff' : 'var(--text-2)',
                    fontSize: 12, fontWeight: 600, marginRight: 12,
                  }}>{i + 1}</span>
                  <span style={{ flex: 1 }}>{r.name}</span>
                  <span style={{ color: 'var(--text-3)', marginRight: 12, fontSize: 12 }}>
                    {r.deals} 单
                  </span>
                  <span style={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                    {fmt(Number(r.revenue ?? 0))}
                  </span>
                </div>
              ))}
              {(ranking ?? []).filter((r: any) => r.role === 'sales').length === 0 && (
                <div style={{ padding: '20px 0', color: 'var(--text-3)', fontSize: 13, textAlign: 'center' }}>
                  暂无销售员数据
                </div>
              )}
            </div>
          </HomeCard>
        </div>
      </div>
    </>
  );
}

const AVATAR_GRADIENTS: Record<number, string> = {
  1: 'linear-gradient(135deg, #6294f5, #3370ff)',
  2: 'linear-gradient(135deg, #51cf8a, #34c759)',
  3: 'linear-gradient(135deg, #d6649e, #c44569)',
  4: 'linear-gradient(135deg, #f6c155, #f0a020)',
  5: 'linear-gradient(135deg, #9b7dd6, #7c4dff)',
  6: 'linear-gradient(135deg, #ee7065, #ee5253)',
  7: 'linear-gradient(135deg, #5eb6e0, #3aafda)',
  8: 'linear-gradient(135deg, #95cc6c, #76b639)',
};

